import { PostgrestError } from '@supabase/supabase-js'

export enum ErrorCategory {
  VALIDATION = 'validation',
  NETWORK = 'network',
  PERMISSION = 'permission',
  NOT_FOUND = 'not_found',
  CONFLICT = 'conflict',
  SERVER = 'server',
  AUTH = 'auth',
  UNKNOWN = 'unknown'
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export class AppError extends Error {
  constructor(
    message: string,
    public code?: string,
    public category: ErrorCategory = ErrorCategory.UNKNOWN,
    public severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    public details?: unknown,
    public userMessage?: string,
    public shouldLog: boolean = true
  ) {
    super(message)
    this.name = 'AppError'
    
    if (this.shouldLog) {
      this.logError()
    }
  }

  private logError() {
    const logData = {
      message: this.message,
      code: this.code,
      category: this.category,
      severity: this.severity,
      details: this.details,
      timestamp: new Date().toISOString(),
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'server',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('AppError:', logData)
    } else {
      // TODO: Send to error monitoring service in production
      console.error('Production Error:', {
        message: this.message,
        code: this.code,
        category: this.category,
        severity: this.severity
      })
    }
  }

  getUserMessage(): string {
    return this.userMessage || this.message
  }

  isRetryable(): boolean {
    return [ErrorCategory.NETWORK, ErrorCategory.SERVER].includes(this.category)
  }
}

export function handleSupabaseError(error: PostgrestError): AppError {
  // Map common Supabase errors to user-friendly messages
  switch (error.code) {
    case 'PGRST116':
      return new AppError(
        'Record not found',
        'NOT_FOUND',
        ErrorCategory.NOT_FOUND,
        ErrorSeverity.MEDIUM,
        error.details,
        'The requested item was not found'
      )
    case '23505':
      return new AppError(
        'This record already exists',
        'DUPLICATE',
        ErrorCategory.CONFLICT,
        ErrorSeverity.MEDIUM,
        error.details,
        'This item already exists. Please try a different name or identifier'
      )
    case '42501':
      return new AppError(
        'You do not have permission to perform this action',
        'PERMISSION_DENIED',
        ErrorCategory.PERMISSION,
        ErrorSeverity.HIGH,
        error.details,
        'You do not have permission to perform this action'
      )
    case '23503':
      return new AppError(
        'Cannot delete record because it is referenced by other records',
        'FOREIGN_KEY_VIOLATION',
        ErrorCategory.CONFLICT,
        ErrorSeverity.MEDIUM,
        error.details,
        'This item cannot be deleted because it is being used by other items'
      )
    case '23514':
      return new AppError(
        'Invalid data provided',
        'CHECK_VIOLATION',
        ErrorCategory.VALIDATION,
        ErrorSeverity.MEDIUM,
        error.details,
        'The data provided does not meet the required format'
      )
    case '42P01':
      return new AppError(
        'Database table not found',
        'TABLE_NOT_FOUND',
        ErrorCategory.SERVER,
        ErrorSeverity.CRITICAL,
        error.details,
        'A system error occurred. Please try again later'
      )
    case 'PGRST301':
      return new AppError(
        'Database connection timeout',
        'CONNECTION_TIMEOUT',
        ErrorCategory.NETWORK,
        ErrorSeverity.HIGH,
        error.details,
        'Connection timeout. Please check your network connection and try again'
      )
    case 'PGRST204':
      return new AppError(
        'No data returned',
        'NO_DATA',
        ErrorCategory.NOT_FOUND,
        ErrorSeverity.LOW,
        error.details,
        'No data found'
      )
    default:
      return new AppError(
        error.message || 'An unexpected database error occurred',
        error.code || 'DB_ERROR',
        ErrorCategory.SERVER,
        ErrorSeverity.HIGH,
        error.details,
        'A database error occurred. Please try again later'
      )
  }
}

export function handleAuthError(error: { message: string }): AppError {
  // Map common auth errors to user-friendly messages
  switch (error.message) {
    case 'Invalid login credentials':
      return new AppError(
        'Invalid email or password',
        'INVALID_CREDENTIALS',
        ErrorCategory.AUTH,
        ErrorSeverity.MEDIUM,
        undefined,
        'Invalid email or password. Please check your credentials and try again'
      )
    case 'Email not confirmed':
      return new AppError(
        'Please check your email and click the confirmation link',
        'EMAIL_NOT_CONFIRMED',
        ErrorCategory.AUTH,
        ErrorSeverity.MEDIUM,
        undefined,
        'Please check your email and click the confirmation link to activate your account'
      )
    case 'User not found':
      return new AppError(
        'No account found with this email address',
        'USER_NOT_FOUND',
        ErrorCategory.AUTH,
        ErrorSeverity.MEDIUM,
        undefined,
        'No account found with this email address'
      )
    case 'Signup not allowed for this instance':
      return new AppError(
        'Account creation is currently disabled',
        'SIGNUP_DISABLED',
        ErrorCategory.AUTH,
        ErrorSeverity.HIGH,
        undefined,
        'Account creation is currently disabled. Please contact support'
      )
    case 'Email rate limit exceeded':
      return new AppError(
        'Too many email attempts',
        'EMAIL_RATE_LIMIT',
        ErrorCategory.AUTH,
        ErrorSeverity.MEDIUM,
        undefined,
        'Too many email attempts. Please wait a few minutes before trying again'
      )
    default:
      return new AppError(
        error.message || 'An authentication error occurred',
        'AUTH_ERROR',
        ErrorCategory.AUTH,
        ErrorSeverity.HIGH,
        undefined,
        'An authentication error occurred. Please try again'
      )
  }
}

// Network error handler
export function handleNetworkError(error: any): AppError {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new AppError(
      'Network connection failed',
      'NETWORK_ERROR',
      ErrorCategory.NETWORK,
      ErrorSeverity.HIGH,
      error,
      'Unable to connect to the server. Please check your internet connection and try again'
    )
  }

  if (error?.code === 'NETWORK_ERROR' || error?.name === 'NetworkError') {
    return new AppError(
      'Network error occurred',
      'NETWORK_ERROR',
      ErrorCategory.NETWORK,
      ErrorSeverity.HIGH,
      error,
      'Network error. Please check your connection and try again'
    )
  }

  return new AppError(
    error?.message || 'Unknown network error',
    'UNKNOWN_NETWORK_ERROR',
    ErrorCategory.NETWORK,
    ErrorSeverity.MEDIUM,
    error,
    'A network error occurred. Please try again'
  )
}

// Validation error handler
export function handleValidationError(errors: Record<string, string[]>): AppError {
  const firstError = Object.values(errors)[0]?.[0] || 'Validation failed'
  const errorCount = Object.keys(errors).length
  
  return new AppError(
    `Validation failed: ${firstError}`,
    'VALIDATION_ERROR',
    ErrorCategory.VALIDATION,
    ErrorSeverity.MEDIUM,
    errors,
    errorCount > 1 
      ? `Please fix ${errorCount} validation errors in the form`
      : firstError
  )
}

// Generic error handler that attempts to categorize unknown errors
export function handleGenericError(error: any): AppError {
  if (error instanceof AppError) {
    return error
  }

  // Try to categorize based on error properties
  let category = ErrorCategory.UNKNOWN
  let severity = ErrorSeverity.MEDIUM
  let userMessage = 'An unexpected error occurred'

  if (error?.name === 'AbortError') {
    category = ErrorCategory.NETWORK
    userMessage = 'Request was cancelled'
  } else if (error?.status >= 400 && error?.status < 500) {
    category = ErrorCategory.VALIDATION
    severity = ErrorSeverity.MEDIUM
    userMessage = 'Invalid request. Please check your input and try again'
  } else if (error?.status >= 500) {
    category = ErrorCategory.SERVER
    severity = ErrorSeverity.HIGH
    userMessage = 'Server error. Please try again later'
  }

  return new AppError(
    error?.message || 'Unknown error occurred',
    error?.code || 'UNKNOWN_ERROR',
    category,
    severity,
    error,
    userMessage
  )
}

// Error recovery suggestions
export function getErrorRecoveryActions(error: AppError): string[] {
  const actions: string[] = []

  switch (error.category) {
    case ErrorCategory.NETWORK:
      actions.push('Check your internet connection')
      actions.push('Try refreshing the page')
      actions.push('Wait a moment and try again')
      break
    case ErrorCategory.VALIDATION:
      actions.push('Check the form for errors')
      actions.push('Ensure all required fields are filled')
      actions.push('Verify data format is correct')
      break
    case ErrorCategory.PERMISSION:
      actions.push('Make sure you are logged in')
      actions.push('Contact support if you believe this is an error')
      break
    case ErrorCategory.NOT_FOUND:
      actions.push('Check if the item still exists')
      actions.push('Try refreshing the page')
      actions.push('Go back and try again')
      break
    case ErrorCategory.SERVER:
      actions.push('Try again in a few minutes')
      actions.push('Contact support if the problem persists')
      break
    default:
      actions.push('Try refreshing the page')
      actions.push('Contact support if the problem continues')
  }

  return actions
}